import { useLayoutEffect, useRef, useState } from 'react';

// On desktop, when a page's own content is shorter than the viewport, the
// footer ends up flush against the bottom of the first screen (the layout
// stretches the content area to fill the gap). That makes the footer pop
// into view on pages that shouldn't show it yet. We measure the content's
// natural (unstretched) height and pad the wrapper just enough to push the
// footer's top past the viewport edge, so it only appears once the user
// scrolls.
export const useHideFooterBelowFold = (enabled: boolean) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [paddingBottom, setPaddingBottom] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) {
      setPaddingBottom(0);
      return;
    }

    const recompute = () => {
      const wrapperEl = wrapperRef.current;
      const contentEl = contentRef.current;
      if (!wrapperEl || !contentEl) {
        return;
      }

      const wrapperTop = wrapperEl.getBoundingClientRect().top + window.scrollY;
      const naturalContentHeight = contentEl.scrollHeight;
      const needed = Math.max(
        0,
        window.innerHeight - wrapperTop - naturalContentHeight,
      );

      setPaddingBottom((prev) => (prev === needed ? prev : needed));
    };

    if (!contentRef.current) {
      return;
    }

    recompute();

    window.addEventListener('resize', recompute);
    const resizeObserver = new ResizeObserver(recompute);
    resizeObserver.observe(contentRef.current);

    return () => {
      window.removeEventListener('resize', recompute);
      resizeObserver.disconnect();
    };
  }, [enabled]);

  return { wrapperRef, contentRef, paddingBottom };
};
